/**
 * Custom hook for managing work notes (comments and actions) functionality
 */

import { useState } from 'react';
import { Ticket, WorkNote } from '../../types/ticketTypes';

export interface UseWorkNotesProps {
  onTicketUpdate?: (ticket: Ticket) => void;
  currentUser?: string;
}

export interface WorkNotesHandlers {
  newUpdate: string;
  setNewUpdate: (value: string) => void;
  handleAddComment: (ticket: Ticket) => void;
  handleAddAction: (ticket: Ticket) => void;
  handleToggleActionCompletion: (ticket: Ticket, workNoteId: string) => void;
}

export const useWorkNotes = ({
  onTicketUpdate,
  currentUser
}: UseWorkNotesProps): WorkNotesHandlers => {
  const [newUpdate, setNewUpdate] = useState('');

  const handleAddComment = (ticket: Ticket) => {
    if (newUpdate.trim()) {
      const newWorkNote: WorkNote = {
        id: `wn-${Date.now()}`,
        type: 'comment',
        content: newUpdate.trim(),
        author: currentUser || 'Unknown User',
        timestamp: new Date().toISOString(),
        isCompleted: false
      };
      
      const updatedTicket = {
        ...ticket,
        workNotes: [...ticket.workNotes, newWorkNote],
        lastComment: newUpdate.trim()
      };
      onTicketUpdate?.(updatedTicket);
      setNewUpdate('');
    }
  };

  const handleAddAction = (ticket: Ticket) => {
    if (newUpdate.trim()) {
      const newWorkNote: WorkNote = {
        id: `wn-${Date.now()}`,
        type: 'action',
        content: newUpdate.trim(),
        author: currentUser || 'Unknown User',
        timestamp: new Date().toISOString(),
        isCompleted: false
      };
      
      const updatedTicket = {
        ...ticket,
        workNotes: [...ticket.workNotes, newWorkNote],
        lastAction: newUpdate.trim()
      };
      onTicketUpdate?.(updatedTicket);
      setNewUpdate('');
    }
  };

  const handleToggleActionCompletion = (ticket: Ticket, workNoteId: string) => {
    const updatedWorkNotes = ticket.workNotes.map(note => 
      note.id === workNoteId && note.type === 'action' 
        ? { ...note, isCompleted: !note.isCompleted }
        : note
    );
    
    const updatedTicket = {
      ...ticket,
      workNotes: updatedWorkNotes
    };
    
    onTicketUpdate?.(updatedTicket);
  };

  return {
    newUpdate,
    setNewUpdate,
    handleAddComment,
    handleAddAction,
    handleToggleActionCompletion
  };
};